import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import { ACCOUNTS } from '../../api/accounts';
import { getInvoicePaidAmount, getOpeningBalance, getOpeningBalancePaid } from '../../api/receiptAllocations';
import { TODAY, daysOverdue, ageBucketOf, oldestUnsettledDate } from '../agents/performanceViz';

const TABS = ['Dashboard', 'Sales', 'Clients', 'Quotations', 'Pro Formas', 'Invoices', 'Commissions'];

// Package sales attribution: most package sales now go straight through InvoiceNew.js
// as a direct invoice (source_type='booking') with NO row in the legacy `bookings` table
// at all — only a minority (still created via the older booking-first flow) have both.
// Counting `bookings` alone silently undercounts any agent whose packages mostly came in
// through the newer direct-invoice path (confirmed live: TLS-33 had 7 package invoices but
// only 1 real `bookings` row). This unions both sources, de-duped by invoices.source_id so
// a booking that DOES have a matching invoice isn't counted twice.
async function loadAgentPackages(agentRefId, { from, to } = {}) {
  const scope = (q) => {
    let r = q.eq('clients.care_of_agent_id', agentRefId);
    if (from) r = r.gte('created_at', from);
    if (to) r = r.lte('created_at', to);
    return r;
  };
  const [{ data: bookingsData }, { data: invoiceData }] = await Promise.all([
    scope(supabase.from('bookings').select('id, booking_ref, total_sell, num_pax, booking_status, created_at, clients!inner(full_name, care_of_agent_id), packages(package_name), sell_currency:sell_currency_id(code)')),
    scope(supabase.from('invoices').select('id, invoice_number, status, source_id, line_items, created_at, clients!inner(full_name, care_of_agent_id)').eq('source_type', 'booking').neq('status', 'VOID')),
  ]);
  const bookingIds = new Set((bookingsData || []).map(b => b.id));
  const lineTotal = (items) => (items || []).reduce((s, li) => s + (parseFloat(li.amount ?? ((li.unitPrice || 0) * (li.qty || 1))) || 0), 0);
  const linePax = (items) => (items || []).reduce((s, li) => s + (parseFloat(li.qty) || 1), 0);

  const fromBookings = (bookingsData || []).map(b => ({
    id: b.id, kind: 'booking', ref: b.booking_ref, client: b.clients?.full_name,
    package: b.packages?.package_name || '—', pax: b.num_pax, total: parseFloat(b.total_sell || 0),
    currency: b.sell_currency?.code, status: b.booking_status, created_at: b.created_at,
  }));
  const fromInvoices = (invoiceData || [])
    .filter(inv => !inv.source_id || !bookingIds.has(inv.source_id))
    .map(inv => ({
      id: inv.id, kind: 'invoice', ref: inv.invoice_number, client: inv.clients?.full_name,
      package: '—', pax: linePax(inv.line_items), total: lineTotal(inv.line_items),
      currency: 'LKR', status: inv.status, created_at: inv.created_at,
    }));
  return [...fromBookings, ...fromInvoices].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
}

// Resolves each commission's reference_id to its real invoice (matching either
// invoices.id or invoices.source_id, same as CommissionLedger.js) and attaches that
// invoice's total/paid/payment-status — so an agent can see not just what she earned,
// but whether the client has actually paid for it yet.
async function resolveCommissionInvoices(list) {
  const refIds = [...new Set(list.map(c => c.reference_id).filter(Boolean))];
  let invoiceByRef = {};
  if (refIds.length) {
    // With a lot of commissions, cramming every unique id into one .or() blows past
    // PostgREST's URL length limit and silently 400s (same root cause as the URL-length
    // bugs already fixed elsewhere in this file). Chunk it.
    const CHUNK = 100;
    let invs = [];
    for (let i = 0; i < refIds.length; i += CHUNK) {
      const slice = refIds.slice(i, i + CHUNK);
      const { data } = await supabase.from('invoices')
        .select('id, invoice_number, status, line_items, source_id, clients(full_name)')
        .or(`id.in.(${slice.join(',')}),source_id.in.(${slice.join(',')})`);
      if (data) invs = invs.concat(data);
    }
    const withStatus = await Promise.all((invs || []).map(async inv => {
      const total = (inv.line_items || []).reduce((s, li) => s + (parseFloat(li.amount ?? ((li.unitPrice || 0) * (li.qty || 1))) || 0), 0);
      const paid = inv.status === 'VOID' ? 0 : await getInvoicePaidAmount(inv.id);
      const balance = total - paid;
      const payStatus = inv.status === 'VOID' ? 'Void' : balance <= 0.01 ? 'Paid' : paid > 0.01 ? 'Partially Paid' : 'Unpaid';
      return { ...inv, total, paid, payStatus };
    }));
    withStatus.forEach(inv => {
      invoiceByRef[inv.id] = inv;
      if (inv.source_id) invoiceByRef[inv.source_id] = inv;
    });
  }
  return list.map(c => ({ ...c, invoice: invoiceByRef[c.reference_id] }));
}

export default function AgentPortal() {
  const { agentRefId, user } = useAuth();
  const username = user?.email?.split('@')[0] || '';
  const [tab, setTab] = useState('Dashboard');

  if (!agentRefId) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">No agent linked to your account</div>
        <div className="empty-state-subtitle">Ask your administrator to link your login to an agent record.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">My Portal</div>
          <div className="page-subtitle">Your sales, clients and commissions</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid var(--border)' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '10px 22px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: tab === t ? 700 : 400,
            color: tab === t ? 'var(--primary)' : 'var(--text-muted)',
            borderBottom: tab === t ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: -2, fontSize: 14,
          }}>{t}</button>
        ))}
      </div>

      {tab === 'Dashboard'   && <AgentDashboard agentRefId={agentRefId} />}
      {tab === 'Sales'       && <AgentSales agentRefId={agentRefId} />}
      {tab === 'Clients'     && <AgentClients agentRefId={agentRefId} />}
      {tab === 'Quotations'  && <AgentQuotations username={username} />}
      {tab === 'Pro Formas'  && <AgentProFormas agentRefId={agentRefId} />}
      {tab === 'Invoices'    && <AgentInvoices agentRefId={agentRefId} />}
      {tab === 'Commissions' && <AgentCommissions agentRefId={agentRefId} />}
    </div>
  );
}

// ─── Dashboard Tab ─────────────────────────────────────────────────────────────

function AgentDashboard({ agentRefId }) {
  const navigate = useNavigate();
  const [loading, setLoading]       = useState(true);
  const [monthStats, setMonthStats] = useState({ tickets: 0, visas: 0, packages: 0, ticketRevenue: 0, visaRevenue: 0, packageRevenue: 0 });
  const [commStats, setCommStats]   = useState({ pending: 0, approved: 0, paid: 0 });
  const [drillDown, setDrillDown]   = useState(null);
  const [drillData, setDrillData]   = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  // "This Month" was the only view — a new agent with a small first month (like Rashid)
  // looked like they had almost no business at all, when their real, total picture (all
  // their invoices/tickets/visas ever) was much bigger. Let the period be chosen instead
  // of hardcoded.
  const [period, setPeriod] = useState('month'); // 'month' | 'all'

  const now = new Date();
  const monthStart = period === 'all' ? null : new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const monthEnd   = period === 'all' ? null : new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  const monthLabel = period === 'all' ? 'All Time' : now.toLocaleString('default', { month: 'long', year: 'numeric' });

  // tickets/visas/bookings carry their OWN care_of_agent_id column too, but it's
  // essentially never populated in practice (confirmed elsewhere in this codebase:
  // 1 of 347 tickets), so filtering directly on it silently drops most of this
  // agent's real sales. Attribution has to go through the client instead — via an
  // embedded-resource filter (clients!inner + .eq('clients.care_of_agent_id', ...))
  // rather than fetching client IDs and using .in(), because an agent can have
  // thousands of clients and .in() with that many UUIDs blows past the URL length
  // limit and Supabase rejects every request with a 400 (this broke Rashid's portal
  // — 1,375 clients — while agents with few clients never hit it).
  useEffect(() => {
    async function load() {
      const cap = monthEnd ? monthEnd + 'T23:59:59' : null;
      const byAgent = (q) => {
        let r = q.eq('clients.care_of_agent_id', agentRefId);
        if (monthStart) r = r.gte('created_at', monthStart);
        if (cap) r = r.lte('created_at', cap);
        return r;
      };

      const [mt, mv, mtr, mvr, pkgRows, comms] = await Promise.all([
        byAgent(supabase.from('tickets').select('id, clients!inner(care_of_agent_id)', { count: 'exact', head: true }).neq('ticket_status', 'VOID')),
        byAgent(supabase.from('visas').select('id, clients!inner(care_of_agent_id)', { count: 'exact', head: true })),
        byAgent(supabase.from('tickets').select('sell_price, clients!inner(care_of_agent_id)').neq('ticket_status', 'VOID')).limit(5000),
        byAgent(supabase.from('visas').select('sell_price, clients!inner(care_of_agent_id)')).limit(5000),
        loadAgentPackages(agentRefId, { from: monthStart, to: cap }),
        supabase.from('commissions').select('commission_amount, commission_status').eq('agent_ref_id', agentRefId),
      ]);

      const sum = (arr, key) => (arr || []).reduce((s, r) => s + parseFloat(r[key] || 0), 0);
      setMonthStats({
        tickets: mt.count || 0, visas: mv.count || 0, packages: pkgRows.length,
        ticketRevenue:  sum(mtr.data, 'sell_price'),
        visaRevenue:    sum(mvr.data, 'sell_price'),
        packageRevenue: pkgRows.reduce((s, r) => s + (r.total || 0), 0),
      });

      const rows = comms.data || [];
      setCommStats({
        pending:  rows.filter(r => r.commission_status === 'PENDING').reduce((s, r)  => s + parseFloat(r.commission_amount || 0), 0),
        approved: rows.filter(r => r.commission_status === 'APPROVED').reduce((s, r) => s + parseFloat(r.commission_amount || 0), 0),
        paid:     rows.filter(r => r.commission_status === 'PAID').reduce((s, r)     => s + parseFloat(r.commission_amount || 0), 0),
      });
      setLoading(false);
    }
    load();
  }, [agentRefId, monthStart, monthEnd]);

  const openDrill = async (type) => {
    if (drillDown === type) { setDrillDown(null); return; }
    setDrillDown(type); setDrillLoading(true); setDrillData([]);
    const cap = monthEnd ? monthEnd + 'T23:59:59' : null;
    const base = (q) => {
      let r = q.eq('clients.care_of_agent_id', agentRefId);
      if (monthStart) r = r.gte('created_at', monthStart);
      if (cap) r = r.lte('created_at', cap);
      return r.order('created_at', { ascending: false }).limit(100);
    };
    let data = [];
    if (type === 'tickets') {
      const { data: d } = await base(supabase.from('tickets').select('id, ticket_ref, airline, route_from, route_to, clients!inner(full_name, care_of_agent_id), sell_price, sell_currency:sell_currency_id(code), payment_status, created_at').neq('ticket_status', 'VOID'));
      data = d || [];
    } else if (type === 'visas') {
      const { data: d } = await base(supabase.from('visas').select('id, visa_ref, clients!inner(full_name, care_of_agent_id), visa_types(name, country), sell_price, sell_currency:sell_currency_id(code), payment_status, visa_status, created_at'));
      data = d || [];
    } else if (type === 'packages') {
      data = (await loadAgentPackages(agentRefId, { from: monthStart, to: cap })).slice(0, 100);
    } else {
      // Commission cards ('PENDING'/'APPROVED'/'PAID') — all-time, not month-scoped.
      const { data: comms } = await supabase.from('commissions').select('*')
        .eq('agent_ref_id', agentRefId).eq('commission_status', type)
        .order('created_at', { ascending: false }).limit(100);
      data = await resolveCommissionInvoices(comms || []);
    }
    setDrillData(data); setDrillLoading(false);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const payColor = (s) => s === 'PAID' ? '#16a34a' : '#dc2626';
  const statusColor = { PENDING: 'badge-yellow', CONFIRMED: 'badge-green', COMPLETED: 'badge-blue', CANCELLED: 'badge-red' };

  return (
    <div>
      {/* Month stats */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {monthLabel} — My Sales
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className={`btn btn-sm ${period === 'month' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setDrillDown(null); setPeriod('month'); }}>This Month</button>
            <button className={`btn btn-sm ${period === 'all' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setDrillDown(null); setPeriod('all'); }}>All Time</button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[
            { key: 'tickets',  label: 'Tickets Issued',    count: monthStats.tickets,  revenue: monthStats.ticketRevenue,  color: '#2563eb', bg: '#eff6ff' },
            { key: 'visas',    label: 'Visa Applications', count: monthStats.visas,    revenue: monthStats.visaRevenue,    color: '#7c3aed', bg: '#f5f3ff' },
            { key: 'packages', label: 'Package Bookings',  count: monthStats.packages, revenue: monthStats.packageRevenue, color: '#0891b2', bg: '#ecfeff' },
          ].map(({ key, label, count, revenue, color, bg }) => (
            <div key={key} onClick={() => openDrill(key)} style={{
              background: drillDown === key ? bg : 'white',
              border: `1.5px solid ${drillDown === key ? color : 'var(--border)'}`,
              borderRadius: 10, padding: '16px 20px', cursor: 'pointer', transition: 'all 0.15s',
            }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color }}>{count}</div>
              <div style={{ fontSize: 12, color, marginTop: 4, fontWeight: 600 }}>LKR {revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{drillDown === key ? '▲ Hide details' : '▼ Click to expand'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Drill-down (sales) */}
      {['tickets', 'visas', 'packages'].includes(drillDown) && (
        <div className="card" style={{ marginTop: 0, borderTop: '3px solid var(--primary)', borderRadius: '0 0 10px 10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              {drillDown === 'tickets' ? 'My Tickets This Month' : drillDown === 'visas' ? 'My Visas This Month' : 'My Bookings This Month'}
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => setDrillDown(null)}>✕ Close</button>
          </div>
          {drillLoading ? (
            <div style={{ padding: 20, textAlign: 'center' }}><div className="spinner" /></div>
          ) : drillData.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No records this month.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
                  {drillDown === 'tickets'  && <><th style={{ textAlign: 'left', padding: '6px 8px' }}>Ref</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Client</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Airline</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Route</th><th style={{ textAlign: 'right', padding: '6px 8px' }}>Sell</th><th style={{ textAlign: 'center', padding: '6px 8px' }}>Payment</th></>}
                  {drillDown === 'visas'    && <><th style={{ textAlign: 'left', padding: '6px 8px' }}>Ref</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Client</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Country</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Type</th><th style={{ textAlign: 'right', padding: '6px 8px' }}>Sell</th><th style={{ textAlign: 'center', padding: '6px 8px' }}>Payment</th></>}
                  {drillDown === 'packages' && <><th style={{ textAlign: 'left', padding: '6px 8px' }}>Ref</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Client</th><th style={{ textAlign: 'left', padding: '6px 8px' }}>Package</th><th style={{ textAlign: 'right', padding: '6px 8px' }}>Total</th><th style={{ textAlign: 'center', padding: '6px 8px' }}>Status</th></>}
                </tr>
              </thead>
              <tbody>
                {drillData.map(row => (
                  <tr key={row.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => navigate(drillDown === 'packages' ? `/${row.kind === 'invoice' ? 'invoices' : 'bookings'}/${row.id}` : `/${drillDown}/${row.id}`)}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    {drillDown === 'tickets' && <>
                      <td style={{ padding: '7px 8px', fontWeight: 600, color: 'var(--primary)' }}>{row.ticket_ref}</td>
                      <td style={{ padding: '7px 8px' }}>{row.clients?.full_name}</td>
                      <td style={{ padding: '7px 8px' }}>{row.airline}</td>
                      <td style={{ padding: '7px 8px', color: 'var(--text-muted)' }}>{row.route_from} → {row.route_to}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'right' }}>{row.sell_currency?.code} {parseFloat(row.sell_price || 0).toLocaleString()}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'center', color: payColor(row.payment_status), fontWeight: 600, fontSize: 11 }}>{row.payment_status}</td>
                    </>}
                    {drillDown === 'visas' && <>
                      <td style={{ padding: '7px 8px', fontWeight: 600, color: 'var(--primary)' }}>{row.visa_ref}</td>
                      <td style={{ padding: '7px 8px' }}>{row.clients?.full_name}</td>
                      <td style={{ padding: '7px 8px' }}>{row.visa_types?.country}</td>
                      <td style={{ padding: '7px 8px', color: 'var(--text-muted)' }}>{row.visa_types?.name}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'right' }}>{row.sell_currency?.code} {parseFloat(row.sell_price || 0).toLocaleString()}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'center', color: payColor(row.payment_status), fontWeight: 600, fontSize: 11 }}>{row.payment_status}</td>
                    </>}
                    {drillDown === 'packages' && <>
                      <td style={{ padding: '7px 8px', fontWeight: 600, color: 'var(--primary)' }}>{row.ref}</td>
                      <td style={{ padding: '7px 8px' }}>{row.client}</td>
                      <td style={{ padding: '7px 8px' }}>{row.package}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'right' }}>{row.currency} {parseFloat(row.total || 0).toLocaleString()}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'center' }}><span className={`badge ${statusColor[row.status] || 'badge-gray'}`}>{row.status}</span></td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Commission summary */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>My Commissions (All Time)</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[
            { key: 'PENDING',  label: 'Pending Approval', value: commStats.pending,  color: '#2563eb', bg: '#eff6ff' },
            { key: 'APPROVED', label: 'Approved',         value: commStats.approved, color: '#d97706', bg: '#fffbeb' },
            { key: 'PAID',     label: 'Paid Out',         value: commStats.paid,     color: '#16a34a', bg: '#f0fdf4' },
          ].map(({ key, label, value, color, bg }) => (
            <div key={label} onClick={() => openDrill(key)} className="card" style={{
              padding: '14px 20px', cursor: 'pointer', transition: 'all 0.15s',
              background: drillDown === key ? bg : 'white',
              border: `1.5px solid ${drillDown === key ? color : 'var(--border)'}`,
            }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color }}>LKR {value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{drillDown === key ? '▲ Hide details' : '▼ Click to expand'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Drill-down (commissions) */}
      {['PENDING', 'APPROVED', 'PAID'].includes(drillDown) && (
        <div className="card" style={{ marginTop: 16, borderTop: '3px solid var(--primary)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>My {drillDown === 'PENDING' ? 'Pending' : drillDown === 'APPROVED' ? 'Approved' : 'Paid'} Commissions</div>
            <button className="btn btn-secondary btn-sm" onClick={() => setDrillDown(null)}>✕ Close</button>
          </div>
          {drillLoading ? (
            <div style={{ padding: 20, textAlign: 'center' }}><div className="spinner" /></div>
          ) : drillData.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No commissions in this status.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Invoice</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Client</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Invoice Paid?</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Invoice Total</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Commission</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {drillData.map(row => (
                  <tr key={row.id} style={{ borderBottom: '1px solid var(--border)', cursor: row.invoice ? 'pointer' : 'default' }}
                    onClick={() => row.invoice && navigate(`/invoices/${row.invoice.id}`)}
                    onMouseEnter={e => { if (row.invoice) e.currentTarget.style.background = 'var(--bg)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td style={{ padding: '7px 8px' }}><span className="badge badge-blue">{row.product_type}</span></td>
                    <td style={{ padding: '7px 8px', fontFamily: 'monospace', fontWeight: 600, color: row.invoice ? 'var(--primary)' : 'var(--text-muted)' }}>{row.invoice?.invoice_number || '—'}</td>
                    <td style={{ padding: '7px 8px', color: 'var(--text-muted)' }}>{row.invoice?.clients?.full_name || '—'}</td>
                    <td style={{ padding: '7px 8px' }}>
                      {row.invoice ? (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, color: row.invoice.payStatus === 'Paid' ? '#16a34a' : row.invoice.payStatus === 'Partially Paid' ? '#d97706' : row.invoice.payStatus === 'Void' ? '#dc2626' : '#64748b', background: row.invoice.payStatus === 'Paid' ? '#f0fdf4' : row.invoice.payStatus === 'Partially Paid' ? '#fffbeb' : row.invoice.payStatus === 'Void' ? '#fef2f2' : '#f1f5f9' }}>
                          {row.invoice.payStatus}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '7px 8px', textAlign: 'right' }}>{row.invoice ? row.invoice.total.toLocaleString() : '—'}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 700 }}>{parseFloat(row.commission_amount || 0).toLocaleString()}</td>
                    <td style={{ padding: '7px 8px', color: 'var(--text-muted)' }}>{row.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sales Tab ────────────────────────────────────────────────────────────────

function AgentSales({ agentRefId }) {
  const [type, setType]     = useState('tickets');
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    // Attribution goes through the client (care_of_agent_id on tickets/visas/bookings
    // themselves is essentially never populated) — via an embedded-resource filter
    // rather than a client-ID prefetch + .in(), which breaks with a 400 once an agent
    // has enough clients to blow past the URL length limit (see dashboard tab above).
    if (type === 'tickets') {
      const { data } = await supabase.from('tickets')
        .select('id, ticket_ref, airline, route_from, route_to, sell_price, sell_currency:sell_currency_id(code), ticket_status, created_at, clients!inner(full_name, care_of_agent_id)')
        .eq('clients.care_of_agent_id', agentRefId)
        .neq('ticket_status', 'VOID')
        .order('created_at', { ascending: false });
      setRows(data || []);
    } else if (type === 'visas') {
      const { data } = await supabase.from('visas')
        .select('id, visa_ref, sell_price, sell_currency:sell_currency_id(code), visa_status, created_at, clients!inner(full_name, care_of_agent_id), visa_types(name)')
        .eq('clients.care_of_agent_id', agentRefId)
        .order('created_at', { ascending: false });
      setRows(data || []);
    } else {
      // Unions legacy `bookings` rows with direct package invoices that never got one —
      // see loadAgentPackages above.
      setRows(await loadAgentPackages(agentRefId));
    }
    setLoading(false);
  }, [agentRefId, type]);

  useEffect(() => { load(); }, [load]);

  const total = rows.reduce((s, r) => s + parseFloat(r.total ?? r.total_sell ?? r.sell_price ?? 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {['tickets', 'visas', 'bookings'].map(t => (
          <button key={t} className={`btn btn-sm ${type === t ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setType(t)} style={{ textTransform: 'capitalize' }}>{t}</button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)', alignSelf: 'center' }}>
          {rows.length} records · Total sell: <strong>{total.toLocaleString()}</strong>
        </span>
      </div>

      <div className="card">
        {loading ? <div className="spinner" /> : (
          <div className="table-wrapper">
            {type === 'tickets' && (
              <table>
                <thead><tr><th>Ref</th><th>Client</th><th>Airline</th><th>Route</th><th>Sell Price</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.ticket_ref}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{r.clients?.full_name}</td>
                      <td>{r.airline}</td>
                      <td style={{ fontSize: 12 }}>{r.route_from} → {r.route_to}</td>
                      <td className="amount">{r.sell_currency?.code} {parseFloat(r.sell_price || 0).toLocaleString()}</td>
                      <td><span className="badge badge-blue">{r.ticket_status}</span></td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No tickets</td></tr>}
                </tbody>
              </table>
            )}
            {type === 'visas' && (
              <table>
                <thead><tr><th>Ref</th><th>Client</th><th>Country</th><th>Sell Price</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.visa_ref}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{r.clients?.full_name}</td>
                      <td>{r.visa_types?.name || '—'}</td>
                      <td className="amount">{r.sell_currency?.code} {parseFloat(r.sell_price || 0).toLocaleString()}</td>
                      <td><span className="badge badge-blue">{r.visa_status}</span></td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No visas</td></tr>}
                </tbody>
              </table>
            )}
            {type === 'bookings' && (
              <table>
                <thead><tr><th>Ref</th><th>Client</th><th>Package</th><th>Pax</th><th>Sell Total</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.ref}</td>
                      <td>{r.client}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{r.package || '—'}</td>
                      <td>{r.pax}</td>
                      <td className="amount">{r.currency} {parseFloat(r.total || 0).toLocaleString()}</td>
                      <td><span className="badge badge-blue">{r.status}</span></td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No bookings</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Clients Tab ───────────────────────────────────────────────────────────────

// Chunked .in() fetch — a big agent (e.g. Rashid, 1,375 clients) blows past PostgREST's
// URL length cap on a single .in() call, which silently 400s and zeroes every downstream
// figure. Same fix already applied elsewhere in this file, factored out since AgentClients
// now needs it for both the balance query and the aging query.
async function fetchAllInChunks(table, selectCols, inColumn, ids, applyFilters) {
  const CHUNK = 150;
  const PAGE = 1000;
  let all = [];
  for (let i = 0; i < ids.length; i += CHUNK) {
    const slice = ids.slice(i, i + CHUNK);
    let from = 0;
    while (true) {
      let q = supabase.from(table).select(selectCols).in(inColumn, slice);
      if (applyFilters) q = applyFilters(q);
      const { data, error } = await q.range(from, from + PAGE - 1);
      if (error || !data || !data.length) break;
      all = all.concat(data);
      if (data.length < PAGE) break;
      from += PAGE;
    }
  }
  return all;
}

function AgentClients({ agentRefId }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('balance');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);
  const [invoicesByClient, setInvoicesByClient] = useState({});
  const [invoicesLoading, setInvoicesLoading] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      // The roster of this agent's clients is just clients.care_of_agent_id directly —
      // deriving it by unioning tickets/visas/bookings (each filtered on THEIR OWN
      // care_of_agent_id, which is essentially never populated) silently missed almost
      // every client. Same root-cause fix as the dashboard/sales tabs above.
      const { data } = await supabase.from('clients').select('id, full_name, client_code, phone').eq('care_of_agent_id', agentRefId);
      const clientList = data || [];
      const clientIds = clientList.map(c => c.id);

      if (clientIds.length > 0) {
        // Scoped strictly to the AR account (1030), not every party=client line — without
        // this, Client Deposits (2010, a liability with the OPPOSITE normal balance) gets
        // blended in and silently understates every client's real receivable.
        const lines = await fetchAllInChunks(
          'journal_lines',
          'party_id, base_debit, base_credit, journal_entries!inner(entry_date, status, is_reversed, reversal_of)',
          'party_id', clientIds,
          q => q.eq('account_id', ACCOUNTS.AR).eq('party_type', 'client').eq('journal_entries.status', 'POSTED'),
        );
        const arRows = lines.filter(l => !l.journal_entries?.is_reversed && !l.journal_entries?.reversal_of);

        const perClient = {};
        arRows.forEach(l => {
          if (!perClient[l.party_id]) perClient[l.party_id] = { debit: 0, credit: 0, lines: [] };
          const c = perClient[l.party_id];
          const debit = Number(l.base_debit || 0), credit = Number(l.base_credit || 0);
          c.debit += debit; c.credit += credit;
          c.lines.push({ date: l.journal_entries.entry_date, debit, credit });
        });

        clientList.forEach(c => {
          const p = perClient[c.id];
          const balance = p ? p.debit - p.credit : 0;
          c.balance = balance;
          if (balance > 0.01) {
            const asOf = oldestUnsettledDate(p.lines) || TODAY.toISOString().slice(0, 10);
            c.ageDays = daysOverdue(asOf);
            c.ageBucket = ageBucketOf(c.ageDays);
          } else {
            c.ageDays = 0;
            c.ageBucket = null;
          }
        });
      }

      setClients(clientList);
      setLoading(false);
    }
    load();
  }, [agentRefId]);

  const loadInvoices = useCallback(async (clientId) => {
    if (invoicesByClient[clientId]) return;
    setInvoicesLoading(clientId);
    try {
      const { data: invs } = await supabase.from('invoices')
        .select('id, invoice_number, issued_date, due_date, status, line_items')
        .eq('client_id', clientId).neq('status', 'VOID')
        .order('issued_date', { ascending: false });

      // getInvoicePaidAmount already nets out receipt_allocations, legacy receipt_vouchers,
      // and FIFO-applied deposits (DEPOSIT_APPLICATION journal entries) — the same helper
      // the rest of the app uses, so "paid" here means the same thing everywhere else it's
      // shown, including any deposit that was applied FIFO rather than to a named invoice.
      const rows = await Promise.all((invs || []).map(async inv => {
        const total = (inv.line_items || []).reduce((s, li) => s + (parseFloat(li.unit_price ?? li.unitPrice ?? li.amount ?? 0) * parseFloat(li.qty ?? li.quantity ?? 1)), 0);
        const paid = await getInvoicePaidAmount(inv.id);
        const balance = total - paid;
        const dueOn = inv.due_date || inv.issued_date;
        const overdueDays = balance > 0.01 ? Math.max(0, daysOverdue(dueOn)) : 0;
        const payStatus = balance <= 0.01 ? 'Paid' : paid > 0.01 ? 'Partially Paid' : 'Unpaid';
        return { ...inv, total, paid, balance, overdueDays, payStatus };
      }));

      // A client's AR balance can carry an Opening Balance too — a lump sum with no
      // invoice row behind it — surfaced as its own pseudo-row so the drill-down totals
      // still reconcile to the balance shown in the list above.
      const [obTotal, obPaid] = await Promise.all([getOpeningBalance(clientId), getOpeningBalancePaid(clientId)]);
      if (obTotal > 0.01) {
        const obBalance = obTotal - obPaid;
        rows.push({
          id: `opening-balance-${clientId}`, invoice_number: 'Opening Balance', issued_date: null,
          total: obTotal, paid: obPaid, balance: obBalance,
          overdueDays: obBalance > 0.01 ? 999 : 0,
          payStatus: obBalance <= 0.01 ? 'Paid' : obPaid > 0.01 ? 'Partially Paid' : 'Unpaid',
        });
      }

      rows.sort((a, b) => (b.issued_date || '').localeCompare(a.issued_date || ''));
      setInvoicesByClient(prev => ({ ...prev, [clientId]: rows }));
    } finally {
      setInvoicesLoading(null);
    }
  }, [invoicesByClient]);

  const toggleExpand = (clientId) => {
    const next = expandedId === clientId ? null : clientId;
    setExpandedId(next);
    if (next) loadInvoices(next);
  };

  const totalDue = clients.reduce((s, c) => s + (c.balance > 0 ? c.balance : 0), 0);
  const aging = clients.reduce((acc, c) => {
    if (c.balance > 0.01 && c.ageBucket) acc[c.ageBucket] = (acc[c.ageBucket] || 0) + c.balance;
    return acc;
  }, {});

  const handleSort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } };
  const sorted = [...clients].sort((a, b) => {
    const av = sortKey === 'full_name' ? (a.full_name || '').toLowerCase() : (a[sortKey] || 0);
    const bv = sortKey === 'full_name' ? (b.full_name || '').toLowerCase() : (b[sortKey] || 0);
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });
  const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const bucketColor = { '0-30': '#16a34a', '31-60': '#d97706', '61-90': '#ea580c', '90+': '#dc2626' };
  const payColor = { Paid: '#16a34a', 'Partially Paid': '#d97706', Unpaid: '#dc2626' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', alignSelf: 'center' }}>
          {clients.length} clients · Total receivable: <strong style={{ color: 'var(--primary)' }}>LKR {totalDue.toLocaleString()}</strong>
        </div>
        <Link to="/clients/new" className="btn btn-primary btn-sm">+ New Client</Link>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          {['0-30', '31-60', '61-90', '90+'].map(b => (
            <div key={b} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: `${bucketColor[b]}15`, color: bucketColor[b], fontWeight: 600 }}>
              {b}d: {(aging[b] || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          ))}
        </div>
      </div>
      <div className="card">
        {loading ? <div className="spinner" /> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('full_name')}>Client{arrow('full_name')}</th>
                  <th>Phone</th>
                  <th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => handleSort('balance')}>Balance Due (LKR){arrow('balance')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('ageDays')}>Age{arrow('ageDays')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(c => (
                  <React.Fragment key={c.id}>
                    <tr onClick={() => toggleExpand(c.id)} style={{ cursor: 'pointer', background: expandedId === c.id ? 'var(--bg)' : '' }}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{c.client_code}</td>
                      <td style={{ fontWeight: 600 }}>{c.full_name}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{c.phone || '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: (c.balance || 0) > 0 ? 'var(--danger)' : 'var(--success)' }}>
                        {(c.balance || 0) > 0 ? (c.balance).toLocaleString() : '—'}
                      </td>
                      <td>
                        {c.ageBucket && <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: `${bucketColor[c.ageBucket]}15`, color: bucketColor[c.ageBucket] }}>{c.ageBucket}d</span>}
                      </td>
                    </tr>
                    {expandedId === c.id && (
                      <tr><td colSpan={5} style={{ padding: 0, background: '#f8fafc' }}>
                        {invoicesLoading === c.id ? (
                          <div style={{ padding: 16, textAlign: 'center' }}><div className="spinner" /></div>
                        ) : (
                          <div style={{ padding: '10px 16px' }}>
                            {(invoicesByClient[c.id] || []).length === 0 ? (
                              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No invoices.</div>
                            ) : (
                              <table style={{ width: '100%', fontSize: 12 }}>
                                <thead>
                                  <tr style={{ color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: 10 }}>
                                    <th style={{ textAlign: 'left', padding: '4px 8px' }}>Invoice</th>
                                    <th style={{ textAlign: 'left', padding: '4px 8px' }}>Issued</th>
                                    <th style={{ textAlign: 'right', padding: '4px 8px' }}>Total</th>
                                    <th style={{ textAlign: 'right', padding: '4px 8px' }}>Paid</th>
                                    <th style={{ textAlign: 'right', padding: '4px 8px' }}>Balance</th>
                                    <th style={{ textAlign: 'center', padding: '4px 8px' }}>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(invoicesByClient[c.id] || []).map(inv => (
                                    <tr key={inv.id} onClick={e => { e.stopPropagation(); if (!String(inv.id).startsWith('opening-balance')) window.location.assign(`/invoices/${inv.id}`); }}
                                      style={{ borderBottom: '1px solid var(--border)', cursor: String(inv.id).startsWith('opening-balance') ? 'default' : 'pointer' }}>
                                      <td style={{ padding: '5px 8px', fontFamily: 'monospace', fontWeight: 600, color: 'var(--primary)' }}>{inv.invoice_number}</td>
                                      <td style={{ padding: '5px 8px', color: 'var(--text-muted)' }}>{inv.issued_date?.slice(0, 10) || '—'}</td>
                                      <td style={{ padding: '5px 8px', textAlign: 'right' }}>{inv.total.toLocaleString()}</td>
                                      <td style={{ padding: '5px 8px', textAlign: 'right', color: '#16a34a' }}>{inv.paid.toLocaleString()}</td>
                                      <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>{inv.balance > 0.01 ? inv.balance.toLocaleString() : '—'}</td>
                                      <td style={{ padding: '5px 8px', textAlign: 'center', color: payColor[inv.payStatus], fontWeight: 600 }}>{inv.payStatus}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
                {clients.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No clients yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Quotations Tab ────────────────────────────────────────────────────────────

function AgentQuotations({ username }) {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data } = await supabase.from('sale_quotations')
        .select('id, quotation_number, title, destination, status, created_at')
        .eq('created_by', username)
        .order('created_at', { ascending: false });
      setRows(data || []);
      setLoading(false);
    }
    load();
  }, [username]);

  const filtered = rows.filter(r =>
    !search ||
    r.quotation_number?.toLowerCase().includes(search.toLowerCase()) ||
    r.title?.toLowerCase().includes(search.toLowerCase()) ||
    r.destination?.toLowerCase().includes(search.toLowerCase())
  );

  const statusColor = { DRAFT: 'badge-blue', SENT: 'badge-yellow', ACCEPTED: 'badge-green', VOID: 'badge-red' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center' }}>
        <input className="form-control" placeholder="Search quotations..." value={search}
          onChange={e => setSearch(e.target.value)} style={{ maxWidth: 300 }} />
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/sale-quotations/new')}>New Quotation</button>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)' }}>{filtered.length} quotations</span>
      </div>
      <div className="card">
        {loading ? <div className="spinner" /> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Ref</th><th>Title</th><th>Destination</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/sale-quotations/${r.id}`)}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: 'var(--primary)' }}>{r.quotation_number}</td>
                    <td>{r.title || '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{r.destination || '—'}</td>
                    <td><span className={`badge ${statusColor[r.status] || 'badge-gray'}`}>{r.status}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No quotations</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Pro Formas Tab ────────────────────────────────────────────────────────────

function AgentProFormas({ agentRefId }) {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data } = await supabase.from('quotations')
        .select('id, quotation_number, status, total_amount, created_at, clients!inner(full_name, client_code, care_of_agent_id), currency:currency_id(code)')
        .eq('clients.care_of_agent_id', agentRefId)
        .order('created_at', { ascending: false });
      setRows(data || []);
      setLoading(false);
    }
    load();
  }, [agentRefId]);

  const filtered = rows.filter(r =>
    !search ||
    r.quotation_number?.toLowerCase().includes(search.toLowerCase()) ||
    r.clients?.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  const statusColor = { DRAFT: 'badge-blue', SENT: 'badge-yellow', ACCEPTED: 'badge-green', VOID: 'badge-red' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center' }}>
        <input className="form-control" placeholder="Search pro formas..." value={search}
          onChange={e => setSearch(e.target.value)} style={{ maxWidth: 300 }} />
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/quotations/new')}>New Pro Forma</button>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)' }}>{filtered.length} pro formas</span>
      </div>
      <div className="card">
        {loading ? <div className="spinner" /> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Ref</th><th>Client</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/quotations/${r.id}`)}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: 'var(--primary)' }}>{r.quotation_number}</td>
                    <td>
                      <div>{r.clients?.full_name || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.clients?.client_code}</div>
                    </td>
                    <td className="amount">{r.currency?.code} {parseFloat(r.total_amount || 0).toLocaleString()}</td>
                    <td><span className={`badge ${statusColor[r.status] || 'badge-gray'}`}>{r.status}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No pro formas for your clients</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Invoices Tab ──────────────────────────────────────────────────────────────

function AgentInvoices({ agentRefId }) {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data } = await supabase.from('invoices')
        .select('id, invoice_number, status, issued_date, line_items, clients!inner(full_name, client_code, care_of_agent_id)')
        .eq('clients.care_of_agent_id', agentRefId)
        .order('created_at', { ascending: false });
      // `status` (DRAFT/SENT/PAID/VOID) is a workflow flag that doesn't automatically
      // flip when a receipt/deposit actually settles the invoice — most of these show
      // 'SENT' even when fully collected. Compute the real paid state the same way
      // the rest of the app does (getInvoicePaidAmount), so agents see whether the
      // money has actually come in, not just the invoice's send-state.
      const withPaid = await Promise.all((data || []).map(async inv => {
        const total = (inv.line_items || []).reduce((s, li) => s + (parseFloat(li.unit_price ?? li.unitPrice ?? li.amount ?? 0) * parseFloat(li.qty ?? li.quantity ?? 1)), 0);
        const paid = inv.status === 'VOID' ? 0 : await getInvoicePaidAmount(inv.id);
        const balance = total - paid;
        const payStatus = inv.status === 'VOID' ? 'Void' : balance <= 0.01 ? 'Paid' : paid > 0.01 ? 'Partially Paid' : 'Unpaid';
        return { ...inv, total, paid, payStatus };
      }));
      setRows(withPaid);
      setLoading(false);
    }
    load();
  }, [agentRefId]);

  const filtered = rows.filter(r =>
    !search ||
    r.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
    r.clients?.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  const statusColor = { DRAFT: 'badge-blue', SENT: 'badge-yellow', PAID: 'badge-green', VOID: 'badge-red', OVERDUE: 'badge-red' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center' }}>
        <input className="form-control" placeholder="Search invoices..." value={search}
          onChange={e => setSearch(e.target.value)} style={{ maxWidth: 300 }} />
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)' }}>{filtered.length} invoices</span>
      </div>
      <div className="card">
        {loading ? <div className="spinner" /> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Invoice #</th><th>Client</th><th>Status</th><th>Payment</th><th style={{ textAlign: 'right' }}>Balance</th><th>Date</th></tr></thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/invoices/${r.id}`)}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: 'var(--primary)' }}>{r.invoice_number}</td>
                    <td>
                      <div>{r.clients?.full_name || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.clients?.client_code}</div>
                    </td>
                    <td><span className={`badge ${statusColor[r.status] || 'badge-gray'}`}>{r.status}</span></td>
                    <td>
                      <span className={`badge ${r.payStatus === 'Paid' ? 'badge-green' : r.payStatus === 'Partially Paid' ? 'badge-yellow' : r.payStatus === 'Void' ? 'badge-red' : 'badge-gray'}`}>
                        {r.payStatus}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>{r.total - r.paid > 0.01 ? (r.total - r.paid).toLocaleString() : '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.issued_date || '—'}</td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No invoices for your clients</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Commissions Tab ───────────────────────────────────────────────────────────

function AgentCommissions({ agentRefId }) {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data } = await supabase.from('commissions')
        .select('*')
        .eq('agent_ref_id', agentRefId)
        .order('created_at', { ascending: false });
      setRows(await resolveCommissionInvoices(data || []));
      setLoading(false);
    }
    load();
  }, [agentRefId]);

  const pending  = rows.filter(r => r.commission_status === 'PENDING').reduce((s, r)  => s + parseFloat(r.commission_amount || 0), 0);
  const approved = rows.filter(r => r.commission_status === 'APPROVED').reduce((s, r) => s + parseFloat(r.commission_amount || 0), 0);
  const paid     = rows.filter(r => r.commission_status === 'PAID').reduce((s, r)     => s + parseFloat(r.commission_amount || 0), 0);

  return (
    <div>
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Pending Approval</div><div className="stat-value amount">{pending.toLocaleString()}</div></div>
        <div className="stat-card"><div className="stat-label">Approved</div><div className="stat-value amount" style={{ color: 'var(--warning)' }}>{approved.toLocaleString()}</div></div>
        <div className="stat-card"><div className="stat-label">Paid</div><div className="stat-value amount" style={{ color: 'var(--success)' }}>{paid.toLocaleString()}</div></div>
      </div>

      <div className="card">
        {loading ? <div className="spinner" /> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Type</th><th>Invoice</th><th>Client</th><th>Invoice Paid?</th><th style={{ textAlign: 'right' }}>Invoice Total</th><th style={{ textAlign: 'right' }}>Commission</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} style={{ cursor: r.invoice ? 'pointer' : 'default' }}
                    onClick={() => r.invoice && navigate(`/invoices/${r.invoice.id}`)}
                    onMouseEnter={e => { if (r.invoice) e.currentTarget.style.background = 'var(--bg)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td><span className="badge badge-blue">{r.product_type}</span></td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: r.invoice ? 'var(--primary)' : 'var(--text-muted)' }}>{r.invoice?.invoice_number || '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{r.invoice?.clients?.full_name || '—'}</td>
                    <td>
                      {r.invoice ? (
                        <span className={`badge ${r.invoice.payStatus === 'Paid' ? 'badge-green' : r.invoice.payStatus === 'Partially Paid' ? 'badge-yellow' : r.invoice.payStatus === 'Void' ? 'badge-red' : 'badge-gray'}`}>
                          {r.invoice.payStatus}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="amount" style={{ textAlign: 'right' }}>{r.invoice ? 'LKR ' + r.invoice.total.toLocaleString() : '—'}</td>
                    <td className="amount" style={{ textAlign: 'right', fontWeight: 700 }}>LKR {parseFloat(r.commission_amount || 0).toLocaleString()}</td>
                    <td>
                      <span className={`badge ${r.commission_status === 'PAID' ? 'badge-green' : r.commission_status === 'APPROVED' ? 'badge-yellow' : 'badge-blue'}`}>
                        {r.commission_status}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No commissions yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
